import React, { useState, useEffect } from "react";
import { supabase } from "./supabase";
import { userService, elogioService, getActiveUniversities } from "./services/supabaseService";
import Toast from "./components/Toast";
import "./style.css";

const DEFAULT_AVATAR = "https://i.pinimg.com/736x/d4/8f/8f/d48f8f59f2de25750b13084b59301201.jpg";

const initialProfile = {
  nome: "Seu Nome",
  status: "solteiro(a)",
  cidade: "",
  quemsoueu: "Estudante",
  etnia: "",
  humor: "extrovertido/a",
  interesses: "amigos, atividades, networking",
  aniversario: "",
  idiomas: "Português",
  moro: "sozinho(a)",
  curso: "",
  periodo: "",
  universidade: "",
  email: "",
  avatar: ""
};

function Profile({ onClose }) {
  const [edit, setEdit] = useState(false);
  const [profile, setProfile] = useState(initialProfile);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showImageCropper, setShowImageCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [toast, setToast] = useState(null);
  const [universities, setUniversities] = useState([]); // Universidades do banco

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
  };

  useEffect(() => {
    loadProfile();
    loadUniversities();
  }, []);

  // Inicializar Google Translate quando o perfil carregar
  useEffect(() => {
    if (!loading) {
      const initTranslate = () => {
        if (window.google && window.google.translate) {
          const element = document.getElementById('google_translate_element_profile');
          if (element && !element.hasChildNodes()) {
            new window.google.translate.TranslateElement({
              pageLanguage: 'pt',
              includedLanguages: 'pt,en,es,fr,de,it,ja,ko,zh-CN,zh-TW',
              layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
              autoDisplay: false
            }, 'google_translate_element_profile');

            // Aguarda o select ser criado e adiciona listener
            setTimeout(() => {
              const select = document.querySelector('#google_translate_element_profile .goog-te-combo');
              if (select) {
                // Restaura idioma salvo
                const savedLang = localStorage.getItem('googleTranslateLanguage');
                if (savedLang && savedLang !== 'pt') {
                  select.value = savedLang;
                  select.dispatchEvent(new Event('change'));
                }

                // Salva quando mudar
                select.addEventListener('change', function() {
                  localStorage.setItem('googleTranslateLanguage', this.value);
                });
              }
            }, 1000);
          }
        }
      };

      // Aguardar um pouco para garantir que o elemento está no DOM
      setTimeout(initTranslate, 500);
    }
  }, [loading]);

  const loadUniversities = async () => {
    try {
      const data = await getActiveUniversities();
      setUniversities(data || []);
    } catch (error) {
    }
  };

  const loadProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return;
      }

      try {
        const userProfile = await userService.getUserById(user.id);
        // Buscar elogios do usuário
        const elogios = await elogioService.getUserElogios(user.id);
        setProfile({ 
          ...initialProfile, 
          ...userProfile,
          elogios: elogios
        });
      } catch (profileErr) {
        // Se o usuário não existe na tabela users, criar com dados básicos
        const newProfile = { 
          ...initialProfile, 
          id: user.id,
          email: user.email,
          nome: user.user_metadata?.nome || user.email?.split('@')[0] || 'Usuário'
        };
        
        try {
          await userService.createUser(newProfile);
          setProfile(newProfile);
        } catch (createErr) {
          setProfile(newProfile); // Usar dados básicos mesmo se falhar
        }
      }
      
      setLoading(false);
    } catch (err) {
      showToast("Erro ao carregar perfil", "error");
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      window.location.href = '/';
    } catch (err) {
      showToast("Erro ao sair", "error");
    }
  };

  const handleChange = (e) => {
    setProfile({ ...profile, [e.target.name]: e.target.value });
  };

  const handleEdit = () => {
    setEdit(true);
  };

  const handleSave = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Validar nome de usuário
      if (!profile.nome || profile.nome.trim().length < 3) {
        showToast("O nome deve ter pelo menos 3 caracteres", "warning");
        setSaving(false);
        return;
      }

      setSaving(true);
      
      // Verificar se o nome já existe em outro usuário
      const users = await userService.getAllUsers();
      const nameExists = users.some(
        userData => 
          userData.id !== user.id && 
          userData.nome && 
          userData.nome.toLowerCase() === profile.nome.toLowerCase()
      );
      
      if (nameExists) {
        showToast("Este nome de usuário já está em uso. Escolha outro", "warning");
        setSaving(false);
        return;
      }

      // Preparar dados para salvar (remover campos que não devem ser enviados)
      const { id, created_at, ...profileToSave } = profile;
      
      await userService.updateUser(user.id, profileToSave);
      setEdit(false);
      showToast("Perfil atualizado com sucesso", "success");
      
      // Recarregar o perfil sem recarregar a página
      await loadProfile();
    } catch (err) {
      showToast("Erro ao salvar perfil. Tente novamente", "error");
    }
    setSaving(false);
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tipo
    if (!file.type.startsWith("image/")) {
      showToast("Por favor, selecione apenas arquivos de imagem!", "warning");
      return;
    }

    // Ler imagem e abrir editor de corte
    const reader = new FileReader();
    reader.onload = (event) => {
      setImageToCrop(event.target.result);
      setShowImageCropper(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCropComplete = async (croppedImageBase64) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    setUploading(true);
    setShowImageCropper(false);
    
    try {
      const updatedProfile = { ...profile, avatar: croppedImageBase64 };
      
      await userService.updateUser(user.id, {
        avatar: croppedImageBase64
      });
      
      setProfile(updatedProfile);
      showToast("Foto de perfil atualizada com sucesso!", "success");
    } catch (uploadError) {
      if (uploadError.code === '406' || uploadError.message?.includes('406')) {
        showToast("Erro 406: Imagem muito grande ou formato inválido. Tente uma imagem menor.", "error");
      } else {
        showToast(`Erro ao enviar foto: ${uploadError.message || 'Tente novamente'}`, "error");
      }
    } finally {
      setUploading(false);
      setImageToCrop(null);
    }
  };

  const handleCropCancel = () => {
    setShowImageCropper(false);
    setImageToCrop(null);
    // Limpar o input file
    const fileInput = document.getElementById('avatar-upload');
    if (fileInput) fileInput.value = '';
  };

  if (loading) {
    return (
      <div className="orkut-profile-layout">
        <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '50px' }}>
          Carregando perfil...
        </div>
      </div>
    );
  }

  return (
    <div className="orkut-profile-layout">
      <aside className="orkut-profile-sidebar">
        <input 
          type="file" 
          id="photoUpload" 
          accept="image/*" 
          onChange={handlePhotoUpload}
          style={{ display: 'none' }}
          disabled={uploading}
        />
        <div 
          className="orkut-profile-avatar" 
          style={{
            backgroundImage: `url(${profile.avatar || DEFAULT_AVATAR})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            cursor: uploading ? 'wait' : 'pointer'
          }}
          onClick={() => !uploading && document.getElementById('photoUpload').click()}
          title={uploading ? "Enviando foto..." : "Clique para alterar foto"}
        />
        
        {uploading && (
          <div style={{ 
            fontSize: '0.9em', 
            color: '#003366', 
            marginTop: '8px',
            textAlign: 'center'
          }}>
            📤 Enviando...
          </div>
        )}
        
        <div className="orkut-profile-nome">
          {edit ? (
            <input 
              name="nome" 
              value={profile.nome} 
              onChange={handleChange}
              className="orkut-profile-input"
              placeholder="Nome completo"
            />
          ) : (
            profile.nome
          )}
        </div>
        
        <div className="orkut-profile-cidade">
          {edit ? (
            <input 
              name="cidade" 
              value={profile.cidade} 
              onChange={handleChange}
              className="orkut-profile-input"
              placeholder="Cidade, Estado"
            />
          ) : (
            profile.cidade
          )}
        </div>

        <div className="orkut-profile-email">
          {profile.email}
        </div>
        
        {!edit && (
          <button className="orkut-profile-edit-btn" onClick={handleEdit}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
            </svg>
            <span>Editar Perfil</span>
          </button>
        )}
        
        {edit && (
          <div className="orkut-profile-edit-actions">
            <button 
              className="orkut-profile-save-btn" 
              onClick={handleSave}
              disabled={saving}
            >
              {!saving && (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V7l-4-4zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm3-10H5V5h10v4z"/>
                </svg>
              )}
              <span>{saving ? "Salvando..." : "Salvar"}</span>
            </button>
            <button 
              className="orkut-profile-cancel-btn" 
              onClick={() => {
                setEdit(false);
                loadProfile();
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
              <span>Cancelar</span>
            </button>
            <button 
              className="orkut-profile-logout-btn" 
              onClick={handleLogout}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
              </svg>
              <span>Sair</span>
            </button>
          </div>
        )}

        {/* Widget Google Translate - Dentro do sidebar */}
        <div className="orkut-translate-widget-container" style={{ marginTop: '16px', width: '100%' }}>
          <div className="orkut-translate-header">
            <span>🌐 Traduzir</span>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              <button 
                className="orkut-hide-bar-btn" 
                onClick={() => {
                  const banner = document.querySelector('.goog-te-banner-frame');
                  const skiptranslate = document.querySelector('body > .skiptranslate');
                  if (banner) banner.style.display = 'none';
                  if (skiptranslate) skiptranslate.style.display = 'none';
                  document.body.style.top = '0';
                  // Salva preferência
                  localStorage.setItem('hideTranslateBar', 'true');
                }}
              >
                Esconder Barra
              </button>
              <button 
                className="orkut-disable-translate-btn" 
                onClick={() => {
                  // Remove idioma salvo
                  localStorage.removeItem('googleTranslateLanguage');
                  // Remove preferência de esconder
                  localStorage.removeItem('hideTranslateBar');
                  // Reseta seletor para português
                  const select = document.querySelector('#google_translate_element_profile .goog-te-combo');
                  if (select) {
                    select.value = '';
                    select.dispatchEvent(new Event('change'));
                  }
                  // Recarrega a página para desativar tradução
                  window.location.reload();
                }}
              >
                Desativar
              </button>
            </div>
          </div>
          <div id="google_translate_element_profile"></div>
        </div>
      </aside>

      <main className="orkut-profile-main">
        <div className="orkut-profile-header-row">
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <div className="orkut-profile-nome-main">{profile.nome}</div>
            {profile.verified && (
              <svg
                width="26"
                height="26"
                viewBox="0 0 24 24"
                fill="#1DA1F2"
                className="verified-badge"
                title="Perfil Verificado"
                style={{ marginLeft: '4px' }}
              >
                <path d="M22.25 12c0-1.43-.88-2.67-2.19-3.34.46-1.39.2-2.9-.81-3.91s-2.52-1.27-3.91-.81c-.66-1.31-1.91-2.19-3.34-2.19s-2.67.88-3.34 2.19c-1.39-.46-2.9-.2-3.91.81s-1.27 2.52-.81 3.91c-1.31.66-2.19 1.91-2.19 3.34s.88 2.67 2.19 3.34c-.46 1.39-.2 2.9.81 3.91s2.52 1.27 3.91.81c.66 1.31 1.91 2.19 3.34 2.19s2.67-.88 3.34-2.19c1.39.46 2.9.2 3.91-.81s1.27-2.52.81-3.91c1.31-.66 2.19-1.91 2.19-3.34zm-11.71 4.2L6.8 12.46l1.41-1.42L10.54 13.38l5.65-5.65 1.42 1.42-7.12 7.05z"/>
              </svg>
            )}
          </div>
        </div>
        
        {/* Seção de Elogios */}
        <div className="orkut-elogios-section">
          <h3 className="orkut-section-title">✨ Meus Elogios</h3>
          <div className="orkut-elogios-grid">
            <div className="orkut-elogio-card">
              <span className="orkut-elogio-label">⭐ Legal</span>
              <span className="orkut-elogio-nota-big">{profile.elogios?.legal || 0}</span>
            </div>
            <div className="orkut-elogio-card">
              <span className="orkut-elogio-label">💙 Confiável</span>
              <span className="orkut-elogio-nota-big">{profile.elogios?.confiavel || 0}</span>
            </div>
            <div className="orkut-elogio-card">
              <span className="orkut-elogio-label">🎨 Criativo</span>
              <span className="orkut-elogio-nota-big">{profile.elogios?.criativo || 0}</span>
            </div>
            <div className="orkut-elogio-card">
              <span className="orkut-elogio-label">🧠 Inteligente</span>
              <span className="orkut-elogio-nota-big">{profile.elogios?.inteligente || 0}</span>
            </div>
            <div className="orkut-elogio-card">
              <span className="orkut-elogio-label">😊 Simpático</span>
              <span className="orkut-elogio-nota-big">{profile.elogios?.simpatico || 0}</span>
            </div>
          </div>
        </div>
        
        <div className="orkut-profile-sections">
          {/* Seção Básica */}
          <div className="orkut-profile-section">
            <h3 className="orkut-section-title">📝 Informações Básicas</h3>
            
            <div className="orkut-profile-field">
              <b>Sobre mim:</b> 
              {edit ? (
                <textarea 
                  name="quemsoueu" 
                  value={profile.quemsoueu} 
                  onChange={handleChange}
                  className="orkut-profile-textarea"
                  rows={3}
                  placeholder="Conte um pouco sobre você..."
                />
              ) : (
                <span>{profile.quemsoueu}</span>
              )}
            </div>

            <div className="orkut-profile-field">
              <b>Status:</b> 
              {edit ? (
                <select 
                  name="status" 
                  value={profile.status} 
                  onChange={handleChange}
                  className="orkut-profile-input"
                >
                  <option value="solteiro(a)">Solteiro(a)</option>
                  <option value="namorando">Namorando</option>
                  <option value="casado(a)">Casado(a)</option>
                  <option value="é complicado">É complicado</option>
                </select>
              ) : (
                <span>{profile.status}</span>
              )}
            </div>

            <div className="orkut-profile-field">
              <b>Cidade:</b> 
              {edit ? (
                <input 
                  name="cidade" 
                  value={profile.cidade} 
                  onChange={handleChange}
                  className="orkut-profile-input"
                  placeholder="Ex: Itajaí, SC"
                />
              ) : (
                <span>{profile.cidade || "não informado"}</span>
              )}
            </div>
          </div>

          {/* Seção Acadêmica */}
          <div className="orkut-profile-section">
            <h3 className="orkut-section-title">🎓 Vida Acadêmica</h3>
            
            <div className="orkut-profile-field">
              <b>Universidade:</b> 
              {edit ? (
                <select 
                  name="universidade" 
                  value={profile.universidade} 
                  onChange={handleChange}
                  className="orkut-profile-input"
                >
                  <option value="">Selecione sua universidade</option>
                  {universities.map(uni => (
                    <option key={uni.id} value={`${uni.nome} (${uni.sigla})`}>
                      {uni.nome} ({uni.sigla})
                    </option>
                  ))}
                </select>
              ) : (
                <span>{profile.universidade || "não informado"}</span>
              )}
            </div>

            <div className="orkut-profile-field">
              <b>Curso:</b> 
              {edit ? (
                <input 
                  name="curso" 
                  value={profile.curso} 
                  onChange={handleChange}
                  className="orkut-profile-input"
                  placeholder="Ex: Ciência da Computação"
                />
              ) : (
                <span>{profile.curso || "não informado"}</span>
              )}
            </div>

            <div className="orkut-profile-field">
              <b>Período:</b> 
              {edit ? (
                <input 
                  name="periodo" 
                  value={profile.periodo} 
                  onChange={handleChange}
                  className="orkut-profile-input"
                  placeholder="Ex: 5º período"
                />
              ) : (
                <span>{profile.periodo || "não informado"}</span>
              )}
            </div>
          </div>

          {/* Seção Pessoal */}
          <div className="orkut-profile-section">
            <h3 className="orkut-section-title">😊 Sobre Mim</h3>
            
            <div className="orkut-profile-field">
              <b>Humor:</b> 
              {edit ? (
                <select 
                  name="humor" 
                  value={profile.humor} 
                  onChange={handleChange}
                  className="orkut-profile-input"
                >
                  <option value="extrovertido(a)">Extrovertido(a)</option>
                  <option value="introvertido(a)">Introvertido(a)</option>
                  <option value="engraçado(a)">Engraçado(a)</option>
                  <option value="sério(a)">Sério(a)</option>
                  <option value="aventureiro(a)">Aventureiro(a)</option>
                  <option value="calmo(a)">Calmo(a)</option>
                </select>
              ) : (
                <span>{profile.humor}</span>
              )}
            </div>
            
            <div className="orkut-profile-field">
              <b>Interesses:</b> 
              {edit ? (
                <input 
                  name="interesses" 
                  value={profile.interesses} 
                  onChange={handleChange}
                  className="orkut-profile-input"
                  placeholder="Ex: esportes, música, tecnologia"
                />
              ) : (
                <span>{profile.interesses}</span>
              )}
            </div>
            
            <div className="orkut-profile-field">
              <b>Aniversário:</b> 
            {edit ? (
              <input 
                name="aniversario" 
                type="date"
                value={profile.aniversario} 
                onChange={handleChange}
                className="orkut-profile-input"
              />
            ) : (
              <span>{profile.aniversario || "não informado"}</span>
            )}
            </div>
          </div>

          {/* Seção Adicional */}
          <div className="orkut-profile-section">
            <h3 className="orkut-section-title">🏠 Outros</h3>
            
            <div className="orkut-profile-field">
              <b>Idiomas:</b> 
            {edit ? (
              <input 
                name="idiomas" 
                value={profile.idiomas} 
                onChange={handleChange}
                className="orkut-profile-input"
                placeholder="Ex: Português, Inglês"
              />
            ) : (
              <span>{profile.idiomas}</span>
            )}
            </div>
            
            <div className="orkut-profile-field">
              <b>Moro:</b> 
              {edit ? (
                <select 
                  name="moro" 
                  value={profile.moro} 
                  onChange={handleChange}
                  className="orkut-profile-input"
                >
                  <option value="sozinho(a)">Sozinho(a)</option>
                  <option value="com família">Com família</option>
                  <option value="com colegas">Com colegas</option>
                  <option value="república">República</option>
                </select>
              ) : (
                <span>{profile.moro}</span>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Image Cropper Modal */}
      {showImageCropper && imageToCrop && (
        <ProfileImageCropper
          image={imageToCrop}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}

      {/* Toast Profissional */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

// Componente de Cropper de Imagem para Perfil (reutilizando a mesma lógica do Home)
function ProfileImageCropper({ image, onCropComplete, onCancel }) {
  const canvasRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const [zoom, setZoom] = React.useState(1);
  const [position, setPosition] = React.useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragStart, setDragStart] = React.useState({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = React.useState(false);
  const imgRef = React.useRef(new Image());

  React.useEffect(() => {
    const img = imgRef.current;
    img.onload = () => {
      setImageLoaded(true);
      
      // Calcular zoom inicial para a imagem preencher bem a área de corte (vertical 3:4)
      const cropWidth = 450;
      const cropHeight = 600;
      const minDimension = Math.min(img.width, img.height);
      const targetSize = cropHeight * 1.2;
      const initialZoom = targetSize / minDimension;
      setZoom(Math.min(Math.max(initialZoom, 0.5), 2));
    };
    img.src = image;
  }, [image]);

  React.useEffect(() => {
    if (imageLoaded) {
      drawCanvas();
    }
  }, [zoom, position, imageLoaded]);

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img.complete) return;

    const ctx = canvas.getContext('2d');
    const cropWidth = 450;  // Largura (3:4 ratio)
    const cropHeight = 600; // Altura
    
    const scaledWidth = img.width * zoom;
    const scaledHeight = img.height * zoom;
    const imgX = (canvas.width - scaledWidth) / 2 + position.x;
    const imgY = (canvas.height - scaledHeight) / 2 + position.y;
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, imgX, imgY, scaledWidth, scaledHeight);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    const cropX = (canvas.width - cropWidth) / 2;
    const cropY = (canvas.height - cropHeight) / 2;
    
    ctx.save();
    ctx.beginPath();
    ctx.rect(cropX, cropY, cropWidth, cropHeight);
    ctx.clip();
    ctx.drawImage(img, imgX, imgY, scaledWidth, scaledHeight);
    ctx.restore();
    
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 3;
    ctx.strokeRect(cropX, cropY, cropWidth, cropHeight);
    
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.lineWidth = 1;
    const thirdW = cropWidth / 3;
    const thirdH = cropHeight / 3;
    ctx.beginPath();
    ctx.moveTo(cropX + thirdW, cropY);
    ctx.lineTo(cropX + thirdW, cropY + cropHeight);
    ctx.moveTo(cropX + thirdW * 2, cropY);
    ctx.lineTo(cropX + thirdW * 2, cropY + cropHeight);
    ctx.moveTo(cropX, cropY + thirdH);
    ctx.lineTo(cropX + cropWidth, cropY + thirdH);
    ctx.moveTo(cropX, cropY + thirdH * 2);
    ctx.lineTo(cropX + cropWidth, cropY + thirdH * 2);
    ctx.stroke();
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };

  const handleMouseUp = () => setIsDragging(false);

  const handleCrop = () => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img.complete) return;
    
    const cropWidth = 450;
    const cropHeight = 600;
    const scaledWidth = img.width * zoom;
    const scaledHeight = img.height * zoom;
    const imgX = (canvas.width - scaledWidth) / 2 + position.x;
    const imgY = (canvas.height - scaledHeight) / 2 + position.y;
    const cropX = (canvas.width - cropWidth) / 2;
    const cropY = (canvas.height - cropHeight) / 2;
    const sourceX = (cropX - imgX) / zoom;
    const sourceY = (cropY - imgY) / zoom;
    const sourceWidth = cropWidth / zoom;
    const sourceHeight = cropHeight / zoom;
    
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
    tempCanvas.width = 338;  // 450 * 0.75 para manter a proporção 3:4
    tempCanvas.height = 450;
    
    tempCtx.drawImage(img, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, 338, 450);
    const croppedImage = tempCanvas.toDataURL('image/jpeg', 0.85);
    onCropComplete(croppedImage);
  };

  React.useEffect(() => {
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousemove', handleMouseMove);
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isDragging, dragStart]);

  return (
    <div className="orkut-modal-bg" onClick={onCancel} style={{ zIndex: 10000 }}>
      <div className="orkut-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', width: '90%' }}>
        <div className="orkut-modal-header">
          <h2>Ajustar Foto de Perfil</h2>
          <button className="orkut-modal-close" onClick={onCancel}>×</button>
        </div>
        <div className="orkut-modal-content">
          <p style={{ textAlign: 'center', marginBottom: '12px', color: '#666', fontSize: '14px' }}>
            Arraste para mover • Use o controle para dar zoom • A foto será cortada em 3:4 (vertical)
          </p>
          
          <div ref={containerRef} style={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'center', marginBottom: '16px', userSelect: 'none' }}>
            <canvas
              ref={canvasRef}
              width={700}
              height={800}
              style={{ width: '100%', maxWidth: '525px', border: '2px solid #ddd', borderRadius: '8px', display: 'block', cursor: isDragging ? 'grabbing' : 'grab', touchAction: 'none' }}
              onMouseDown={handleMouseDown}
            />
          </div>

          <div style={{ marginBottom: '16px', padding: '0 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button onClick={() => setZoom(Math.max(0.5, zoom - 0.1))} style={{ background: '#003366', color: '#fff', border: 'none', width: '36px', height: '36px', borderRadius: '50%', cursor: 'pointer', fontSize: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>-</button>
              <input type="range" min="0.5" max="3" step="0.1" value={zoom} onChange={(e) => setZoom(parseFloat(e.target.value))} style={{ flex: 1, height: '6px', borderRadius: '3px', outline: 'none', cursor: 'pointer' }} />
              <button onClick={() => setZoom(Math.min(3, zoom + 0.1))} style={{ background: '#003366', color: '#fff', border: 'none', width: '36px', height: '36px', borderRadius: '50%', cursor: 'pointer', fontSize: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
              <span style={{ minWidth: '50px', textAlign: 'center', fontSize: '14px', color: '#666' }}>{Math.round(zoom * 100)}%</span>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
            <button className="orkut-btn-primary" onClick={handleCrop} style={{ minWidth: '140px' }}>Confirmar Corte</button>
            <button className="orkut-btn-secondary" onClick={onCancel} style={{ minWidth: '100px' }}>Cancelar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Profile;
